import { type ApiKey, type OrganizationAccess, type User, type UserOrganization } from '@/entities'
import { DEMO_ORGANIZATION_ID } from '@/entities/organization/Organization'
import { organizationScopes } from '@/entities/OrganizationAccess'
import { ObjectId } from '@/utils/objectid'

type GetOrganizationAccessInput = {
    user?: User | null
    apiKey?: ApiKey | null
    organizationId: ObjectId
}

export function getOrganizationAccess(
    input: GetOrganizationAccessInput & { apiKey: ApiKey }
): OrganizationAccess | undefined
export function getOrganizationAccess(
    input: GetOrganizationAccessInput & { apiKey?: null }
): UserOrganization | undefined
export function getOrganizationAccess(
    input: GetOrganizationAccessInput
): OrganizationAccess | UserOrganization | undefined
export function getOrganizationAccess({
    user,
    apiKey,
    organizationId,
}: GetOrganizationAccessInput): OrganizationAccess | UserOrganization | undefined {
    let organizationAccess: OrganizationAccess | UserOrganization | undefined = undefined

    if (apiKey) {
        if (apiKey.organization.equals(organizationId)) {
            organizationAccess = {
                organization: apiKey.organization,
                owner: false,
                portal: false,
                permissions: apiKey.permissions,
                restrictions: apiKey.restrictions,
                customAccess: {},
                role: null,
            }
        }
    } else if (user) {
        organizationAccess = user.organizations.find(({ organization: o }) => o.equals(organizationId))
        if (!organizationAccess) {
            const hasSystemOrganizationReadPermission = user.systemPermissions.includes('organization.read')
            const hasSystemOrganizationWritePermission = user.systemPermissions.includes('organization.write')
            const isDemoOrganization = organizationId.equals(DEMO_ORGANIZATION_ID)

            if (isDemoOrganization) {
                organizationAccess = {
                    organization: DEMO_ORGANIZATION_ID,
                    owner: false,
                    portal: true,
                    permissions: {
                        ...Object.fromEntries(
                            organizationScopes.map((scope) => [
                                scope,
                                {
                                    read: true,
                                    insert: true,
                                    edit: true,
                                    remove: true,
                                },
                            ])
                        ),
                        contactPassTemplate: {
                            read: true,
                            insert: false,
                            edit: true,
                            remove: false,
                        },
                        contactIssuedPass: {
                            read: true,
                            insert: false,
                            edit: true,
                            remove: false,
                        },
                        organization: {
                            read: true,
                            insert: false,
                            edit: false,
                            remove: false,
                        },
                    },
                    restrictions: {},
                    roomMemberships: [],
                    customAccess: {},
                    contactId: null,
                    tags: [],
                    invitationType: null,
                    contacts: [],
                    role: null,
                }
            } else if (hasSystemOrganizationReadPermission || hasSystemOrganizationWritePermission) {
                organizationAccess = {
                    organization: organizationId,
                    owner: hasSystemOrganizationWritePermission,
                    portal: true,
                    permissions:
                        hasSystemOrganizationReadPermission && !hasSystemOrganizationWritePermission
                            ? Object.fromEntries(
                                  organizationScopes.map((scope) => [
                                      scope,
                                      {
                                          read: hasSystemOrganizationReadPermission,
                                          insert: hasSystemOrganizationWritePermission,
                                          edit: hasSystemOrganizationWritePermission,
                                          remove: hasSystemOrganizationWritePermission,
                                      },
                                  ])
                              )
                            : {},
                    restrictions: {},
                    roomMemberships: [],
                    customAccess: {},
                    contactId: null,
                    tags: [],
                    invitationType: null,
                    contacts: [],
                    role: null,
                    _impersonatedFromSystemPermissions: true,
                }
            }
        }
    }

    return organizationAccess
}
